/**
 * Live yield widgets — Phase 2C-i.5b.
 *
 *  <MilestoneCountdown />        — per-milestone achievement / projection
 *  <ReclaimedScheduleTracker />  — reclaim or delay vs contractual wait
 *  <LiveYieldPanel />            — combined wrapper, fetches yield_settings
 *
 * All widgets:
 *  - Pro-tier gated (Hidden for demo/starter).
 *  - Read ONLY from monitored StrengthEstimates. Never from predicted.
 *  - Distinguish "achieved" (verified from history crossing) from "estimated"
 *    (projection from the latest 3-point slope).
 *  - No finance-dashboard tone. Engineering wording throughout.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Activity, AlertTriangle } from "lucide-react";

import { useTier } from "@/contexts/AuthContext";
import { authService } from "@/services/authService";

const PULSE_KEYFRAME_INJECT = (() => {
    if (typeof document === "undefined") return null;
    const id = "cc-yield-pulse-style";
    if (document.getElementById(id)) return null;
    const style = document.createElement("style");
    style.id = id;
    style.textContent = `
        @keyframes cc-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.55; } }
        .cc-pulse { animation: cc-pulse 1s ease-in-out infinite; }
    `;
    document.head.appendChild(style);
    return true;
})();

/* ---------- helpers ---------- */

const fmtNumber = (n, d = 1) =>
    (n === null || n === undefined || Number.isNaN(n))
        ? "—"
        : Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: d });

const fmtRemaining = (hoursFromNow) => {
    if (hoursFromNow == null || hoursFromNow < 0) return "—";
    const h = Math.floor(hoursFromNow);
    const m = Math.round((hoursFromNow - h) * 60);
    return `${h} h ${m} min`;
};

/** Map milestone_type → field on monitoredEstimate.milestones for the
 *  precomputed achievement time (canonical, written by the solver/ingest
 *  layer). Falls back to history-interpolated crossing when null. */
const MILESTONE_FIELD_BY_TYPE = {
    formwork_strike: "formwork_release_at_h",
    falsework_removal: "formwork_release_at_h",
    pt_stressing: "pt_window_at_h",
};

/** Canonical helper. Single source of truth for "when did this milestone
 *  achieve?" — used by every widget so the countdown, yield record, and
 *  any future surface read the same number. Phase 2C-ii.1 P2 fix.
 */
const getMilestoneAchievementHours = (monitoredEstimate, milestoneType, requiredStrengthMPa) => {
    if (!monitoredEstimate) return null;
    const field = MILESTONE_FIELD_BY_TYPE[milestoneType];
    const precomputed = field ? monitoredEstimate?.milestones?.[field] : null;
    if (precomputed != null && precomputed > 0) return precomputed;
    return findCrossing(monitoredEstimate?.history, requiredStrengthMPa);
};

/** Return the first time in `history` where `fc_est` first reaches `target`,
 *  using linear interpolation between consecutive samples. Null if never crossed. */
const findCrossing = (history, target) => {
    if (!Array.isArray(history) || history.length < 2) return null;
    for (let i = 1; i < history.length; i++) {
        const a = history[i - 1], b = history[i];
        if (a.fc_est == null || b.fc_est == null) continue;
        if (a.fc_est >= target) return a.t;
        if (b.fc_est >= target && a.fc_est < target) {
            const span = b.fc_est - a.fc_est;
            if (span <= 0) return b.t;
            const frac = (target - a.fc_est) / span;
            return a.t + frac * (b.t - a.t);
        }
    }
    return null;
};

/** Linear extrapolation from the last 3 sample points. Returns the time at
 *  which the line reaches `target`, or null if slope ≤ 0 / target already met /
 *  projection > 7 days out. */
const extrapolateToTarget = (history, target) => {
    if (!Array.isArray(history) || history.length < 3) return null;
    const last3 = history.slice(-3).filter((p) => p.fc_est != null && p.t != null);
    if (last3.length < 3) return null;
    // Least-squares slope/intercept on (t, fc_est)
    const mean = (xs) => xs.reduce((a, b) => a + b, 0) / xs.length;
    const ts = last3.map((p) => p.t);
    const ys = last3.map((p) => p.fc_est);
    const tBar = mean(ts);
    const yBar = mean(ys);
    let num = 0, den = 0;
    for (let i = 0; i < ts.length; i++) {
        num += (ts[i] - tBar) * (ys[i] - yBar);
        den += (ts[i] - tBar) ** 2;
    }
    if (den === 0) return null;
    const m = num / den;
    if (m <= 0) return null;
    const b = yBar - m * tBar;
    const tCross = (target - b) / m;
    const lastT = ts[ts.length - 1];
    if (tCross <= lastT) return null;        // target already past last point — should be "achieved"
    if (tCross - lastT > 168) return null;   // projection > 7 days out
    return tCross;
};

const Pill = ({ tone = "teal", testid, children }) => {
    const cls = {
        teal:  "border-[#01696f] text-[#01696f] bg-[rgba(1,105,111,0.06)]",
        mono:  "border-[#5a6669] text-[#5a6669] bg-transparent",
        amber: "border-[#b3741f] text-[#b3741f] bg-[rgba(179,116,31,0.06)]",
    }[tone];
    return (
        <span data-testid={testid} className={`inline-flex items-center tc-mono text-[9.5px] tracking-[0.16em] uppercase px-1.5 py-[2px] border ${cls} leading-none whitespace-nowrap`}>
            {children}
        </span>
    );
};

/* ===================== MilestoneCountdown ===================== */

export const MilestoneCountdown = ({ pour, monitoredEstimate, milestones, projectId }) => {
    const { tier } = useTier();
    if (tier !== "professional") return null;

    if (!milestones || milestones.length === 0) {
        return (
            <SetupPrompt projectId={projectId} testid="milestone-countdown-setup-prompt">
                No milestones configured · Configure milestones in Yield &amp; ROI to surface achievement countdowns →
            </SetupPrompt>
        );
    }

    if (!monitoredEstimate || !Array.isArray(monitoredEstimate.history)) return null;

    const rows = milestones
        .map((m) => {
            const target = Number(m.required_strength_MPa);
            if (!target || target <= 0) return null;
            const achievedAt = getMilestoneAchievementHours(monitoredEstimate, m.milestone_type, target);
            if (achievedAt != null) {
                return { state: "achieved", milestone: m, at_h: achievedAt };
            }
            const lastSample = monitoredEstimate.history[monitoredEstimate.history.length - 1];
            const projected = extrapolateToTarget(monitoredEstimate.history, target);
            if (projected == null || !lastSample) return null;  // State C — hidden
            return {
                state: "projected",
                milestone: m,
                at_h: projected,
                remaining_h: projected - lastSample.t,
                last_t: lastSample.t,
            };
        })
        .filter(Boolean);

    if (rows.length === 0) return null;

    const computedAt = monitoredEstimate.computed_at
        ? new Date(monitoredEstimate.computed_at)
        : null;
    const startWallTime = computedAt
        ? new Date(computedAt.getTime() - (monitoredEstimate.history?.[monitoredEstimate.history.length - 1]?.t || 0) * 3.6e6)
        : null;
    const wallAt = (t_h) =>
        startWallTime
            ? new Date(startWallTime.getTime() + t_h * 3.6e6).toLocaleString()
            : `T+${fmtNumber(t_h, 1)} h`;

    return (
        <div
            data-testid={`milestone-countdown-${pour?.id || "default"}`}
            className="border border-[#d8dcdd] bg-[#ffffff] p-5 flex flex-col gap-3"
        >
            <div className="flex items-baseline justify-between">
                <span className="tc-mono text-[10px] tracking-widest uppercase text-[#5a6669]">
                    Milestone countdown · in-place strength
                </span>
                <span className="tc-mono text-[10px] text-[#8a9498]">
                    from monitored StrengthEstimate
                </span>
            </div>

            {rows.map((r) => (
                <div
                    key={r.milestone.milestone_type + r.milestone.label}
                    data-testid={`milestone-countdown-row-${r.milestone.milestone_type}`}
                    className="flex items-baseline justify-between gap-3 border-t border-[#d8dcdd] pt-3"
                >
                    {r.state === "achieved" ? (
                        <>
                            <div className="tc-mono text-[12px] text-[#0e1416] leading-[1.5]">
                                <span className="font-medium">{r.milestone.label}</span>
                                <span className="text-[#5a6669]"> ({r.milestone.required_strength_MPa} MPa) </span>
                                achieved at{" "}
                                <span className="text-[#01696f] font-medium">T+{fmtNumber(r.at_h, 1)} h</span>
                                <span className="text-[#8a9498]"> ({wallAt(r.at_h)})</span>
                            </div>
                            <Pill tone="teal" testid={`milestone-countdown-pill-${r.milestone.milestone_type}`}>
                                <CheckCircle2 size={10} strokeWidth={1.5} className="mr-1" />
                                Achieved · monitored
                            </Pill>
                        </>
                    ) : (
                        <>
                            <div className="tc-mono text-[12px] text-[#0e1416] leading-[1.5]">
                                <span className="font-medium">{r.milestone.label}</span>
                                <span className="text-[#5a6669]"> ({r.milestone.required_strength_MPa} MPa) </span>
                                — estimated in{" "}
                                <span data-testid={`milestone-countdown-remaining-${r.milestone.milestone_type}`}
                                      className="text-[#01696f] font-medium cc-pulse">
                                    {fmtRemaining(r.remaining_h)}
                                </span>{" "}
                                <span className="text-[#8a9498]">
                                    (approx. T+{fmtNumber(r.at_h, 1)} h, {wallAt(r.at_h)})
                                </span>
                            </div>
                            <Pill tone="mono" testid={`milestone-countdown-pill-${r.milestone.milestone_type}`}>
                                <Activity size={10} strokeWidth={1.5} className="mr-1" />
                                Estimated · monitored extrapolation
                            </Pill>
                        </>
                    )}
                </div>
            ))}
        </div>
    );
};

/* ===================== ReclaimedScheduleTracker ===================== */

export const ReclaimedScheduleTracker = ({ pour, monitoredEstimate, milestones, settingsLoaded, projectId }) => {
    const { tier } = useTier();
    if (tier !== "professional") return null;

    if (!settingsLoaded) {
        return (
            <SetupPrompt projectId={projectId} testid="reclaim-tracker-setup-prompt">
                Configure Yield &amp; ROI to surface reclaim tracking →
            </SetupPrompt>
        );
    }
    if (!milestones || milestones.length === 0) return null;
    if (!monitoredEstimate || !Array.isArray(monitoredEstimate.history)) return null;

    const lastSample = monitoredEstimate.history[monitoredEstimate.history.length - 1];

    const rows = milestones
        .map((m) => {
            const target = Number(m.required_strength_MPa);
            const wait = Number(m.contractual_wait_duration_hours);
            if (!target || !wait) return null;
            const achievedAt = getMilestoneAchievementHours(monitoredEstimate, m.milestone_type, target);
            if (achievedAt != null) {
                // State A — reclaim confirmed if pre-contractual; otherwise omitted (already late, not a "reclaim")
                if (achievedAt < wait) {
                    return { state: "A_reclaim_confirmed", milestone: m, achieved_h: achievedAt, gap_h: wait - achievedAt };
                }
                return null;
            }
            const projected = extrapolateToTarget(monitoredEstimate.history, target);
            const lastT = lastSample?.t ?? 0;
            if (projected != null && projected <= wait) {
                return { state: "B_projected_reclaim", milestone: m, projected_h: projected, gap_h: wait - projected };
            }
            // Either projection > wait, or projection unavailable + already past wait
            if ((projected != null && projected > wait) || (projected == null && lastT > wait)) {
                const delayHours = projected != null
                    ? projected - wait
                    : Math.max(0, lastT - wait);
                return { state: "C_delay", milestone: m, delay_h: delayHours };
            }
            return null;  // not enough info
        })
        .filter(Boolean);

    if (rows.length === 0) return null;

    const positiveTotal = rows
        .filter((r) => r.state !== "C_delay")
        .reduce((acc, r) => acc + (r.gap_h || 0), 0);

    return (
        <div
            data-testid={`reclaim-tracker-${pour?.id || "default"}`}
            className="border border-[#d8dcdd] bg-[#ffffff] p-5 flex flex-col gap-3"
        >
            <div className="flex items-baseline justify-between">
                <span className="tc-mono text-[10px] tracking-widest uppercase text-[#5a6669]">
                    Reclaimed schedule · in-place
                </span>
                <span
                    data-testid="reclaim-tracker-aggregate"
                    className="tc-mono text-[11px] text-[#0e1416]"
                >
                    {positiveTotal > 0
                        ? <>Total reclaim across milestones:{" "}
                            <span className="text-[#01696f] font-medium">
                                {fmtNumber(positiveTotal / 24, 2)} days · {fmtNumber(positiveTotal, 1)} h
                            </span></>
                        : "No reclaim across configured milestones"}
                </span>
            </div>

            {rows.map((r) => {
                const m = r.milestone;
                if (r.state === "A_reclaim_confirmed") return (
                    <Row key={m.milestone_type + m.label} testid={`reclaim-tracker-row-${m.milestone_type}`}>
                        <span>
                            Current reclaim:{" "}
                            <span className="text-[#01696f] font-medium">
                                {fmtNumber(r.gap_h / 24, 2)} days ({fmtNumber(r.gap_h, 1)} h)
                            </span>{" "}
                            <span className="text-[#5a6669]">· {m.label}</span>
                        </span>
                        <Pill tone="teal" testid={`reclaim-tracker-pill-${m.milestone_type}`}>
                            <CheckCircle2 size={10} strokeWidth={1.5} className="mr-1" /> Reclaim confirmed
                        </Pill>
                    </Row>
                );
                if (r.state === "B_projected_reclaim") return (
                    <Row key={m.milestone_type + m.label} testid={`reclaim-tracker-row-${m.milestone_type}`}>
                        <span>
                            Projected reclaim:{" "}
                            <span className="text-[#01696f] font-medium">
                                {fmtNumber(r.gap_h / 24, 2)} days ({fmtNumber(r.gap_h, 1)} h)
                            </span>{" "}
                            <span className="text-[#5a6669]">· {m.label} · if achieved as estimated</span>
                        </span>
                        <Pill tone="mono" testid={`reclaim-tracker-pill-${m.milestone_type}`}>
                            <Activity size={10} strokeWidth={1.5} className="mr-1" /> Projected · monitored extrapolation
                        </Pill>
                    </Row>
                );
                return (
                    <Row key={m.milestone_type + m.label} testid={`reclaim-tracker-row-${m.milestone_type}`}>
                        <span className="text-[#b3741f]">
                            Projected delay:{" "}
                            <span className="font-medium">
                                {fmtNumber(r.delay_h / 24, 2)} days ({fmtNumber(r.delay_h, 1)} h)
                            </span>{" "}
                            <span className="text-[#5a6669]">· {m.label}</span>
                        </span>
                        <Pill tone="amber" testid={`reclaim-tracker-pill-${m.milestone_type}`}>
                            <AlertTriangle size={10} strokeWidth={1.5} className="mr-1" /> Delay risk
                        </Pill>
                    </Row>
                );
            })}
        </div>
    );
};

const Row = ({ children, testid }) => (
    <div
        data-testid={testid}
        className="flex items-baseline justify-between gap-3 border-t border-[#d8dcdd] pt-3 tc-mono text-[12px] text-[#0e1416] leading-[1.5]"
    >
        {children}
    </div>
);

const SetupPrompt = ({ projectId, testid, children }) => (
    <div
        data-testid={testid}
        className="border border-dashed border-[#d8dcdd] bg-[#f6f7f6] p-4 tc-mono text-[11.5px] text-[#5a6669]"
    >
        <Link
            to={`/app/projects/${projectId}/yield`}
            className="text-[#01696f] hover:text-[#015a60] transition-colors"
        >
            {children}
        </Link>
    </div>
);

/* ===================== LiveYieldPanel (combined wrapper) ===================== */

/**
 * Fetches yield_settings for the project and renders both widgets. Use this on
 * ThermalTab Panel 7 + MaturityTab Monitored card. Hidden for non-Pro.
 */
export const LiveYieldPanel = ({ pour, monitoredEstimate, projectId }) => {
    const { tier } = useTier();
    const [settings, setSettings] = useState(null);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        if (tier !== "professional" || !projectId) {
            setLoaded(true);
            return () => { cancelled = true; };
        }
        (async () => {
            try {
                const { data } = await authService.http.get(`/projects/${projectId}/yield_settings`);
                if (!cancelled) setSettings(data);
            } catch {
                if (!cancelled) setSettings(null);
            } finally {
                if (!cancelled) setLoaded(true);
            }
        })();
        return () => { cancelled = true; };
    }, [projectId, tier]);

    if (tier !== "professional") return null;
    if (!loaded) return null;

    const milestones = settings?.milestones || [];
    const settingsLoaded = settings?.id != null;  // null id => default placeholder, not persisted

    return (
        <div data-testid={`live-yield-panel-${pour?.id || "default"}`} className="mt-4 flex flex-col gap-3">
            <MilestoneCountdown
                pour={pour}
                monitoredEstimate={monitoredEstimate}
                milestones={milestones}
                projectId={projectId}
            />
            <ReclaimedScheduleTracker
                pour={pour}
                monitoredEstimate={monitoredEstimate}
                milestones={milestones}
                settingsLoaded={settingsLoaded}
                projectId={projectId}
            />
        </div>
    );
};

export default LiveYieldPanel;
